/**
 * 업무프로세스 맵 샘플 — 단계(업무)가 주인이고, 화면은 단계에 연결한다.
 * 선행·후속·bypass는 단계 단위. 화면(screenId)은 진입점만 가진다.
 */

export const SAMPLE_PROCESS_IDS = {
  FORMATION: 'BUDGET_FORMATION',
  EXPENSE: 'EXPENSE_EXEC',
};

/** 기관 옵션(샘플) — 단계 id 기준 */
export const SAMPLE_OPTION_STEPS = {
  MONTHLY_BUDGET: 'MONTHLY_BUDGET',
  EXPENSE_PROPOSAL: 'EXPENSE_PROPOSAL',
};

/**
 * @typedef {{
 *   screenId: string,
 *   primary?: boolean,
 *   note?: string,
 * }} ProcessScreenLink
 *
 * @typedef {{
 *   id: string,
 *   label: string,
 *   role: string,
 *   nodeKind: 'SCREEN'|'DIALOG'|'ACTION'|'SYSTEM',
 *   optional?: boolean,
 *   summary: string,
 *   screens: ProcessScreenLink[],
 * }} ProcessStep
 *
 * @typedef {{
 *   id: string,
 *   label: string,
 *   summary: string,
 *   steps: ProcessStep[],
 * }} ProcessDef
 */

/** @type {ProcessDef[]} */
export const SAMPLE_PROCESSES = [
  {
    id: SAMPLE_PROCESS_IDS.FORMATION,
    label: '예산편성',
    summary: '차수 등록 후 편성·(월별예산)·확정으로 현액을 만든다.',
    steps: [
      {
        id: 'BUDGET_ROUND',
        label: '예산차수등록',
        role: '예산담당',
        nodeKind: 'SCREEN',
        summary: '회계연도 차수를 열고 편성 일정을 관리한다.',
        screens: [{ screenId: 'BUD_ROUND_REG', primary: true }],
      },
      {
        id: 'BUDGET_FORMATION',
        label: '예산편성등록',
        role: '사업부서·예산담당',
        nodeKind: 'SCREEN',
        summary: '부서 요구와 조정을 통해 편성액을 확정 초안으로 만든다.',
        screens: [
          { screenId: 'BUD_EXP_REQ', primary: true, note: '지출 요구' },
          { screenId: 'BUD_EXP_ADJ', note: '지출 조정' },
          { screenId: 'BUD_INC_REQ', note: '수입 요구' },
          { screenId: 'BUD_INC_ADJ', note: '수입 조정' },
        ],
      },
      {
        id: SAMPLE_OPTION_STEPS.MONTHLY_BUDGET,
        label: '월별예산',
        role: '사업부서',
        nodeKind: 'DIALOG',
        optional: true,
        summary: '편성액을 월별로 배분한다. 별도 메뉴가 아니라 편성 화면 팝업이다.',
        screens: [
          {
            screenId: 'BUD_EXP_REQ',
            primary: true,
            note: '지출편성요구 — 월별예산 팝업',
          },
          {
            screenId: 'BUD_INC_REQ',
            note: '수입편성요구 — 월별예산 팝업',
          },
        ],
      },
      {
        id: 'BUDGET_CONFIRM',
        label: '예산확정',
        role: '예산담당',
        nodeKind: 'ACTION',
        summary: '차수 확정 처리. 확정 시 현액이 생성된다.',
        screens: [
          {
            screenId: 'BUD_ROUND_REG',
            primary: true,
            note: '차수등록 화면의 확정 처리',
          },
        ],
      },
      {
        id: 'BUDGET_BALANCE',
        label: '현액',
        role: '예산·사업부서',
        nodeKind: 'SYSTEM',
        summary: '확정 결과. 집행(품의·결의)의 한도 기준이 된다.',
        screens: [{ screenId: 'BUD_BALANCE', primary: true, note: '현액 조회' }],
      },
    ],
  },
  {
    id: SAMPLE_PROCESS_IDS.EXPENSE,
    label: '지출집행',
    summary: '현액 한도에서 품의(옵션) 후 결의·지급으로 집행한다.',
    steps: [
      {
        id: 'EXEC_BALANCE',
        label: '예산확정(현액)',
        role: '예산·사업부서',
        nodeKind: 'SYSTEM',
        summary: '집행 선행. 편성 프로세스에서 만든 현액을 한도로 쓴다.',
        screens: [{ screenId: 'BUD_BALANCE', primary: true }],
      },
      {
        id: SAMPLE_OPTION_STEPS.EXPENSE_PROPOSAL,
        label: '지출품의',
        role: '사업부서',
        nodeKind: 'SCREEN',
        optional: true,
        summary: '집행 계획 품의. 미사용 기관은 현액에서 결의로 바로 간다.',
        screens: [
          { screenId: 'EXP_PROP_REG', primary: true, note: '등록' },
          { screenId: 'EXP_PROP_APPROVE', note: '승인' },
        ],
      },
      {
        id: 'EXPENSE_RESOLUTION',
        label: '지출결의',
        role: '사업부서·지출담당',
        nodeKind: 'SCREEN',
        summary: '원인행위·증빙을 묶어 지출을 확정한다.',
        screens: [
          { screenId: 'EXP_RES_REG', primary: true, note: '등록' },
          { screenId: 'EXP_RES_REG_T1', note: '등록 type1' },
          { screenId: 'EXP_RES_APPROVE', note: '승인' },
        ],
      },
      {
        id: 'EXPENSE_PAYMENT',
        label: '지급명령',
        role: '지출담당',
        nodeKind: 'SCREEN',
        summary: '승인된 결의에 대해 지급을 확정한다.',
        screens: [{ screenId: 'EXP_PAY_ORDER', primary: true }],
      },
    ],
  },
];

export const NODE_KIND_LABEL = {
  SCREEN: '화면',
  DIALOG: '팝업',
  ACTION: '처리',
  SYSTEM: '결과',
};

export function getSampleProcess(processId) {
  return SAMPLE_PROCESSES.find((p) => p.id === processId) || SAMPLE_PROCESSES[0];
}

/** bypass된 옵션 단계를 제외한 유효 경로 */
export function getEffectiveSteps(process, bypassedStepIds) {
  const skipped = bypassedStepIds || new Set();
  return (process?.steps || []).filter((step) => {
    if (!step.optional) return true;
    return !skipped.has(step.id);
  });
}

export function getStepNeighbors(effectiveSteps, stepId) {
  const idx = effectiveSteps.findIndex((s) => s.id === stepId);
  if (idx < 0) return { prev: null, next: null, onPath: false };
  return {
    onPath: true,
    prev: idx > 0 ? effectiveSteps[idx - 1] : null,
    next: idx < effectiveSteps.length - 1 ? effectiveSteps[idx + 1] : null,
  };
}

export function primaryScreen(step) {
  if (!step?.screens?.length) return null;
  return step.screens.find((s) => s.primary) || step.screens[0];
}
